import {
  Column,
  FlexGrid as Grid,
  InlineLoading,
  InlineNotification,
  Row,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@carbon/react';
import { OutputListSkeleton } from '../common/output_list_skeleton';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  TitleCalculationResponse,
  TitleWorkspaceResponse,
} from 'types/api-responses';
import { sumBy } from 'lodash-es';
import { Date } from 'components/date';
import { useEffect, useState } from 'react';
import { DownloadButton } from 'components/download-button';
import { OpenPanelFilledLeft, OpenPanelLeft } from '@carbon/react/icons';
import { useDownloadResource } from 'lib/hooks/useDownloadFile';
import { SelectedAsset, SubTractsList, WellsList } from './by-asset';

import style from './calculation.module.scss';
import { OwnerList, SelectedOwner } from './by-owner';
import {
  calculationDateAtom,
  calculationsMapAtom,
  lastCalculationAtom,
  lastCalculationUriAtom,
  selectedOutputAtom,
} from './atoms';
import { CalculateForm } from './calculate-form';
import classNames from 'classnames';
import { DECIMAL_FORMATTER_TWO_FRACTIONS } from 'lib/ui';
import { subscribe } from 'lib/pusher';
import {
  titleCalculationActivityAtom,
  titleCalculationPusherErrorMessageAtom,
} from 'atoms/title-calculation-atoms';
import { CalculateFormProgress } from 'types/calculate-form-progress';
import { TitleWorkspaceAtomType } from '../atoms';

const JSDate = globalThis.Date;

const getCalculatedAcreage = (calculation?: TitleCalculationResponse) => {
  if (calculation && !(calculation.tracts && calculation.tracts.length)) {
    return null;
  }

  const allHaveAcreage = calculation?.tracts.every(
    (tract) => tract.calculatedAcreage !== null
  );

  return allHaveAcreage
    ? sumBy(calculation?.tracts, 'calculatedAcreage')
    : null;
};

const DownloadOwnershipButton = ({
  downloadHref,
  calculation,
}: {
  downloadHref: string;
  calculation: TitleCalculationResponse | undefined;
}) => {
  const downloadOwnership = useDownloadResource(downloadHref);

  return calculation?.status === 'processed' ? (
    <DownloadButton
      loading={downloadOwnership.generatingDownload}
      loadingText="Generating Ownership Report"
      onClick={() => downloadOwnership.triggerDownload()}
    >
      Download Ownership
    </DownloadButton>
  ) : null;
};

export function CalculationImpl({
  titleWorkspace,
  calculation,
  progress,
  isTitlePusherNotificationActive,
}: {
  titleWorkspace: TitleWorkspaceResponse;
  calculation: TitleCalculationResponse | undefined;
  progress: number;
  isTitlePusherNotificationActive: boolean;
}) {
  const [isActive, setIsActive] = useState(false);
  const [selectedOutput, setSelectedOutput] = useAtom(selectedOutputAtom);

  const calculatedAcreage = calculation && getCalculatedAcreage(calculation);
  const isAsset = selectedOutput === 'assets';

  const lastTag = titleWorkspace.resources.lastTag;

  const handleClick = () => {
    setIsActive((current) => !current);
  };

  return (
    <Grid condensed fullWidth className={style.calculationContent}>
      <Row>
        <Column
          md={isActive ? 0 : calculation ? 2 : 16}
          className={style.sidePanel}
        >
          <aside>
            <div className={style.sideHeader}>
              <CalculateForm loading={Boolean(lastTag && !calculation)} />
              {!calculation ? <OutputListSkeleton /> : null}
              {calculation && (
                <div className={style.typeOfOutput}>
                  <Tabs
                    selectedIndex={isAsset ? 0 : 1}
                    onChange={({ selectedIndex }) => {
                      setSelectedOutput(
                        selectedIndex === 0 ? 'assets' : 'owners'
                      );
                    }}
                  >
                    <TabList contained>
                      <Tab id="tab-assets">Assets</Tab>
                      <Tab id="tab-owners">Owners</Tab>
                    </TabList>
                    <TabPanels>
                      <TabPanel>
                        {selectedOutput === 'assets' ? (
                          <>
                            <SubTractsList />
                            <WellsList />
                          </>
                        ) : null}
                      </TabPanel>
                      <TabPanel>
                        {selectedOutput === 'owners' ? (
                          <OwnerList
                            entitiesHref={calculation.resources.entities.href}
                          />
                        ) : null}
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </div>
              )}
            </div>
          </aside>
        </Column>

        <Column
          md={isActive ? 8 : 6}
          className={isActive ? style.trackPanelOpen : style.trackPanel}
        >
          <div className={style.downloadOwnershipRow}>
            <a className={style.closePanelBtn} onClick={handleClick}>
              {isActive ? <OpenPanelLeft /> : <OpenPanelFilledLeft />}
            </a>
            {calculation && (
              <DownloadOwnershipButton
                downloadHref={calculation.resources.downloads.href}
                calculation={calculation}
              />
            )}
          </div>
          <div className={classNames(style.titleArea)}>
            <div className={style.titleHeader}>
              <h2 className={style.titleWorkspace}>Title Workspace</h2>
              {isTitlePusherNotificationActive &&
                (progress > 0 ? (
                  <>
                    <progress
                      value={progress}
                      max="100"
                      className={style.progressBar}
                    />
                    <span className={style.calculatingText}>
                      Calculating...
                    </span>
                  </>
                ) : (
                  <InlineLoading
                    role="status"
                    aria-label="Calculating..."
                    description="Calculating..."
                    className={style.calculatingLoader}
                  />
                ))}
            </div>
            <div className={style.titleStats}>
              <p
                data-testid="calculation-date"
                className={style.dateCalculation}
              >
                <span>Last Calculated: </span>
                <Date date={calculation?.createdAt} format={'lll'} showTime />
              </p>
              <p className={style.dateCalculation}>
                <span>With As-of Date: </span>
                <Date date={calculation?.asOfDate} />
              </p>
              {calculatedAcreage && (
                <p className={style.dateCalculation}>
                  <span>Total Title GIS Gross Acreage: </span>
                  {DECIMAL_FORMATTER_TWO_FRACTIONS.format(calculatedAcreage)}
                </p>
              )}
            </div>
          </div>
          <div className={style.contentArea}>
            {isAsset ? (
              <SelectedAsset />
            ) : (
              <SelectedOwner titleWorkspace={titleWorkspace} />
            )}
          </div>
        </Column>
      </Row>
    </Grid>
  );
}

export function Calculation({
  titleWorkspace,
  titleWorkspaceAtom,
}: {
  titleWorkspace: TitleWorkspaceResponse;
  titleWorkspaceAtom: TitleWorkspaceAtomType;
}) {
  const calculationDate = useAtomValue(calculationDateAtom);
  const calculation = useAtomValue(lastCalculationAtom);
  const setCalculationsMap = useSetAtom(calculationsMapAtom);
  const setLastCalculationUri = useSetAtom(lastCalculationUriAtom);
  const getTitleWorkspace = useSetAtom(titleWorkspaceAtom);
  const [isTitlePusherNotificationActive, setIsTitlePusherNotificationActive] =
    useState(false);
  const [progress, setProgress] = useState(0);
  const [
    titleCalculationPusherErrorMessage,
    setTitleCalculationPusherErrorMessage,
  ] = useAtom(titleCalculationPusherErrorMessageAtom);
  const setCalculationActivity = useSetAtom(titleCalculationActivityAtom);

  const lastTagHref = titleWorkspace.resources.lastTag?.href;
  const lastTagHrefWithDate = lastTagHref
    ? `${lastTagHref}${calculationDate ? '?date=' + calculationDate : ''}`
    : undefined;
  const calculationResult =
    calculation && calculation.data && !('error' in calculation.data)
      ? calculation.data
      : undefined;

  const channel =
    titleWorkspace.channel || calculationResult?.notifications.channel;
  const event = calculationResult?.notifications.events[0].name || 'change';
  const calculationHref =
    titleWorkspace.resources.currentCalculation?.href ||
    calculationResult?.href;

  useEffect(() => {
    if (lastTagHrefWithDate) {
      setLastCalculationUri(lastTagHrefWithDate);
      setCalculationsMap({
        href: lastTagHrefWithDate,
      });
    }
  }, [setLastCalculationUri, setCalculationsMap, lastTagHrefWithDate]);

  useEffect(() => {
    if (!channel || !calculationHref) {
      return () => void 0;
    }

    if (titleWorkspace.channel) {
      setIsTitlePusherNotificationActive(true);
      setCalculationActivity((prev) => {
        if (prev.isCalculating) return prev;
        return {
          isCalculating: true,
          startedAt: new JSDate().toISOString(),
          completedAt: null,
          progress: 0,
        };
      });
    }

    const subscription = subscribe(channel);
    subscription.bind(event, (data: CalculateFormProgress) => {
      if ('calcStatus' in data && 'completionPercentage' in data) {
        if (typeof data.completionPercentage === 'number') {
          setProgress(data.completionPercentage);
        }
        setCalculationActivity((prev) => ({
          ...prev,
          progress:
            typeof data.completionPercentage === 'number'
              ? data.completionPercentage
              : prev.progress,
        }));
        if (data.calcStatus === 'error') {
          setIsTitlePusherNotificationActive(false);
          setTitleCalculationPusherErrorMessage(data.errorMessage);
          setCalculationActivity((prev) => {
            if (!prev.isCalculating) return prev;
            return {
              ...prev,
              isCalculating: false,
              completedAt: new JSDate().toISOString(),
            };
          });
          subscription.unsubscribe();
        }

        if (data.calcStatus === 'complete' || data.calcStatus === 'cancelled') {
          setIsTitlePusherNotificationActive(false);
          setCalculationActivity((prev) => {
            if (!prev.isCalculating) return prev;
            return {
              ...prev,
              isCalculating: false,
              completedAt: new JSDate().toISOString(),
            };
          });
          getTitleWorkspace();
        }
      }
    });
    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (titleCalculationPusherErrorMessage.length > 0) {
    return (
      <InlineNotification
        kind="error"
        title={titleCalculationPusherErrorMessage}
        lowContrast
      />
    );
  }

  return (
    <CalculationImpl
      calculation={calculationResult}
      titleWorkspace={titleWorkspace}
      progress={progress}
      isTitlePusherNotificationActive={isTitlePusherNotificationActive}
    />
  );
}
